import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {RegistryPreferences, SequenceSchemaType, TagSchema} from 'constants/RegistryConstants';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ExtraActionButtons} from 'metadata/FieldActionButtons';
import preferences from 'Preferences';
import {
  RegistrySummaryQuery,
  RegistrySummaryQueryVariables,
} from 'registry/graphql/__generated/RegistrySummaryQuery';
import {REGISTRY_SUMMARY_QUERY} from 'registry/graphql/RegistrySummary';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {
  FieldMigrationCreationModal,
  FieldMigrationSettings,
} from 'schemas/fields/field-migration/FieldMigrationCreationModal';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {ImplementedStaticSchemaInterface, SchemaInterface} from 'schemas/SchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import * as TagSchemaActions from 'tag-schema/TagSchemaActions';
import legacyRunModal from 'ui-library/legacyRunModal';

import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import EntityFieldAdvancedSettingsIcon, {generateSchemaFieldUpdate} from './EntityFieldAdvancedSettingsIcon';
import {getFieldOptions} from './FieldTableOptionGetters';
import FieldTableRow, {isMultiSelectDisabled} from './FieldTableRow';
export interface FileFieldTableRowProps {
  id: string;
  fileType?: string;
  itemType?: TagSchemaItemType | null;
  sequenceType: SequenceSchemaType | null;
  tagSchemas?: Array<TagSchemaSummaryGraphQL>;
  storableSchemas?: Array<StorableSchema>;
  schemaInterfaces: Array<SchemaInterface>;
  implementedStaticSchemaInterfaces?: Array<ImplementedStaticSchemaInterface>;
  schemaFieldSelectors: Array<SchemaFieldSelector>;
  initialField: SchemaField;
  onCancel: (id: string) => void;
  onEdit: (id: string) => void;
  refetchSchemaAfterUpdate: () => Promise<void>;
  requestChange: (id: string, fieldWithChanges: SchemaField) => void;
  requestRemove: (id: string) => void;
  ownerUrlId: string;
  registryId?: string | null;
  schemaId: string;
  schema: TagSchema | null;
  schemaNumItems?: number;
  availableBaseMoleculeSchemaIds?: Set<string>;
  requestPopulateFromAnotherField?: (destinationField: SchemaField) => void;
  requestFieldMigrationCreation?: (
    fieldApiId: string,
    destinationField: SchemaField,
    locale?: string
  ) => Promise<void>;
}

interface QueryingProps extends FileFieldTableRowProps {
  // Proxy for a feature gate on sequence link types (part links, translation links) being enabled.
  // Should only be set by QueryingFileFieldTableRow.
  sequenceLinksEnabled: boolean;
}

function FileFieldTableRow(props: QueryingProps): JSX.Element {
  // Enable deleting and archiving entry schema fields, if user has
  // `ENABLE_ENTRY_SCHEMA_REMOVE_FIELDS` preference set
  // or deploy has ENABLE_ENTRY_SCHEMA_REMOVE_FIELDS == True
  const areRemoveAndArchiveEnabled =
    props.fileType !== ModelTypeConstants.ENTRY ||
    preferences.get(RegistryPreferences.ENABLE_ENTRY_SCHEMA_REMOVE_FIELDS) ||
    BenchlingConfig.ENABLE_ENTRY_SCHEMA_REMOVE_FIELDS;

  const extraActionButtons: ExtraActionButtons = {viewMode: [], editMode: []};
  const schema = props.schema;
  const isLongSequence =
    props.fileType === ModelTypeConstants.SEQUENCE &&
    (props.sequenceType === SequenceSchemaType.DNA_SEQUENCE ||
      props.sequenceType === SequenceSchemaType.RNA_SEQUENCE);
  const includeSequenceLinks = props.sequenceLinksEnabled && isLongSequence;
  const includeDnaSequenceSpecificLinks =
    includeSequenceLinks && props.sequenceType === SequenceSchemaType.DNA_SEQUENCE;
  const includeProteinLinks = props.fileType === ModelTypeConstants.PROTEIN;
  const includeMoleculeLinks = props.fileType === ModelTypeConstants.MOLECULE;

  const fieldTypeOptions = getFieldOptions({
    initialField: props.initialField,
    schemaInterfaces: props.schemaInterfaces,
    implementedStaticSchemaInterfaces: props.implementedStaticSchemaInterfaces,
    schemaFieldSelectors: props.schemaFieldSelectors,
    storableSchemas: props.storableSchemas ?? [], // FIXME(maybe) legacy menu includes generic inventory link even if there are no storable schemas
    tagSchemas: props.tagSchemas ?? [],
    includeBooleanField: true,
    includeSnapshotField: false,
    includeJSONField: false,
    includeArchivedSchemas: true,
    includeSequenceLinks,
    includeDnaSequenceSpecificLinks,
    includeProteinLinks,
    includeMoleculeLinks,
    sequenceSchemaType: props.sequenceType,
    availableBaseMoleculeSchemaIds: props.availableBaseMoleculeSchemaIds,
  });

  const schemaIsMixtureSchema =
    (props.schema?.folderItemType ?? props.fileType) === ModelTypeConstants.MIXTURE;

  // Don't show advanced settings options when we're creating a schema
  if (schema?.id != null) {
    extraActionButtons.viewMode.push(
      <EntityFieldAdvancedSettingsIcon
        onUpdateField={async (update) => {
          const updateParams = generateSchemaFieldUpdate(update, props.initialField, schema);
          await TagSchemaActions.update(schema.id, updateParams);
          props.refetchSchemaAfterUpdate();
        }}
        field={props.initialField}
        schema={schema}
        schemaNumItems={props.schemaNumItems}
        key="advanced-settings"
        requestPopulateFromAnotherField={props.requestPopulateFromAnotherField}
        handleFieldMigration={() => {
          legacyRunModal(FieldMigrationCreationModal, {
            initialField: props.initialField,
            newField: props.initialField,
            availableFieldOptions: fieldTypeOptions,
            onRequestSubmit: (settings: FieldMigrationSettings) => {
              props.requestFieldMigrationCreation!(
                settings.field.apiId,
                {...settings.field},
                settings.locale
              );
            },
            onRequestClose: () => {},
            schemaFieldSelectors: props.schemaFieldSelectors,
            shouldShowIsIntegration: false,
            shouldShowIsParentLink: false,
            isFieldDefinitionEditable: true,
            numItems: props.schemaNumItems || null,
          });
        }}
        disableMultiSelect={isMultiSelectDisabled(props.initialField, schemaIsMixtureSchema)}
        itemType={props.itemType}
        schemaFieldSelectors={props.schemaFieldSelectors}
      />
    );
  }

  return (
    <FieldTableRow
      availableFieldOptions={fieldTypeOptions}
      areRemoveAndArchiveEnabled={areRemoveAndArchiveEnabled}
      {...props}
      shouldShowWarehouseWarning={true}
      extraActionButtons={extraActionButtons}
      isUneditable={props.initialField.isUneditable}
      shouldShowTooltipText={true}
      advancedSettingsComponentForFieldCreation={EntityFieldAdvancedSettingsIcon}
      schemaIsMixtureSchema={schemaIsMixtureSchema}
    />
  );
}

function QueryingFileFieldTableRow(props: FileFieldTableRowProps): JSX.Element {
  // TODO: a new query specifying only registry.useStrictValidation should be used
  const registrySummaryResult = useSkippableBenchlingQuery<
    RegistrySummaryQuery,
    RegistrySummaryQueryVariables
  >(REGISTRY_SUMMARY_QUERY, {
    variables: {registryId: props.registryId!},
    skip: props.registryId == null,
  });
  if (registrySummaryResult.type === QueryResultType.SUCCESS) {
    assert(!isInaccessible(registrySummaryResult.data.registry), 'Expecting registry to be accessible');
    return (
      <FileFieldTableRow
        {...props}
        // Revisit whether registry.useStrictValidation is the best way to determine if sequence link types
        // are enabled. See PR #21101.
        sequenceLinksEnabled={registrySummaryResult.data.registry.useStrictValidation}
      />
    );
  } else {
    return <FileFieldTableRow {...props} sequenceLinksEnabled={false} />;
  }
}

export default QueryingFileFieldTableRow;
